import { assertNever } from 'owa-assert';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getFileInfo, GetSharingInfoResponseBase } from 'owa-fileprovider-link-services';
import { HttpStatusCode, isSuccessStatusCode } from 'owa-http-status-codes';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import AttachmentResultCode from 'owa-service/lib/contract/AttachmentResultCode';
import GetSharingInfoRequest from 'owa-service/lib/contract/GetSharingInfoRequest';
import GetSharingInfoResponse from 'owa-service/lib/contract/GetSharingInfoResponse';
import SharingLinkKind from 'owa-service/lib/contract/SharingLinkKind';
import getSharingInfoRequest from 'owa-service/lib/factory/getSharingInfoRequest';
import getSharingInfoResponse from 'owa-service/lib/factory/getSharingInfoResponse';
import getSharingInformationOperation from 'owa-service/lib/operation/getSharingInformationOperation';
import { trace } from 'owa-trace';
import {
    OneDriveProSharingLinkInfoResponse,
    SharingLinkInfoResponse,
    SharingLinkProviderType,
    SharingLinkType,
} from '../types/SharingLinkInfoResponse';
import { getSharingLinkInfo } from './getSharingLinkInfo';

function createRequest(attachmentUrl: string): GetSharingInfoRequest {
    return getSharingInfoRequest({ attachmentUrl: attachmentUrl });
}

export function getSharingInformation(
    attachmentUrl: string,
    providerType: AttachmentDataProviderType
): Promise<GetSharingInfoResponseBase> {
    switch (providerType) {
        case AttachmentDataProviderType.OneDrivePro:
            if (isFeatureEnabled('doc-attachment-sharingLinkInfoOWSPrimeService')) {
                return getSharingInfoFromOWSPrime(attachmentUrl);
            }
            return getSharingInformationOperation(createRequest(attachmentUrl));
        case AttachmentDataProviderType.OneDriveConsumer:
        case AttachmentDataProviderType.Dropbox:
        case AttachmentDataProviderType.Box:
            return getFileInfo(attachmentUrl, providerType);
        default:
            throw new Error(`getSharingInformation do not support ${providerType}`);
    }
}

async function getSharingInfoFromOWSPrime(attachmentUrl: string): Promise<GetSharingInfoResponse> {
    const rawResponse = await getSharingLinkInfo(attachmentUrl, true);

    if (isSuccessStatusCode(rawResponse.status)) {
        const response: SharingLinkInfoResponse = JSON.parse(await rawResponse.text());

        if (isOneDriveProSharingLinkInfoResponse(response)) {
            const [anonymousEditLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.AnonymousEdit
            );
            const [anonymousViewLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.AnonymousView
            );
            const [organizationEditLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.OrganizationEdit
            );
            const [organizationViewLink] = response.sharingLinks.filter(
                sl => sl.type === SharingLinkType.OrganizationView
            );

            return getSharingInfoResponse({
                AccessRequestMode: response.accessRequestMode,
                AnonymousEditLink: anonymousEditLink ? anonymousEditLink.url : undefined,
                AnonymousViewLink: anonymousViewLink ? anonymousViewLink.url : undefined,
                CanCreateAnonymousEditLink: response.canCreateAnonymousEditLink,
                CanCreateAnonymousViewLink: response.canCreateAnonymousViewLink,
                CanCreateOrganizationEditLink: response.canCreateOrganizationEditLink,
                CanCreateOrganizationViewLink: response.canCreateOrganizationViewLink,
                CanShareExternally: response.canShareExternally,
                CanShareInternally: response.canShareInternally,
                CanonicalUrl: response.canonicalUrl,
                ExpirationDate: response.expirationTime,
                FileName: response.name,
                IsAnonymousEditLinkAvailable: !!anonymousEditLink,
                IsAnonymousViewLinkAvailable: !!anonymousViewLink,
                IsOrganizationEditLinkAvailable: !!organizationEditLink,
                IsOrganizationViewLinkAvailable: !!organizationViewLink,
                MimeType: undefined,
                OrganizationEditLink: organizationEditLink ? organizationEditLink.url : undefined,
                OrganizationViewLink: organizationViewLink ? organizationViewLink.url : undefined,
                ResultCode: AttachmentResultCode.Success,
                SharingLinkKind: convertSharingLinkTypeToSharingLinkKind(response.linkType),
            });
        }

        throw new Error('Invalid response from OWS` from sharing link info');
    } else if (rawResponse.status === HttpStatusCode.NotFound) {
        return getSharingInfoResponse({
            ResultCode: AttachmentResultCode.NotFound,
        });
    } else if (rawResponse.status === 400) {
        return getSharingInfoResponse({
            ResultCode: AttachmentResultCode.InvalidUrl,
        });
    } else {
        trace.warn(`Invalid response for OWS' get share info. Status code: ${rawResponse.status}`);
        return getSharingInfoResponse({
            ResultCode: AttachmentResultCode.GenericFailure,
        });
    }
}

function isOneDriveProSharingLinkInfoResponse(
    response: SharingLinkInfoResponse
): response is OneDriveProSharingLinkInfoResponse {
    return response.providerType === SharingLinkProviderType.OneDrivePro;
}

function convertSharingLinkTypeToSharingLinkKind(type: SharingLinkType): number {
    switch (type) {
        case SharingLinkType.None:
            return SharingLinkKind.Uninitialized;
        case SharingLinkType.View:
            return SharingLinkKind.Direct;
        case SharingLinkType.Edit:
            return SharingLinkKind.Direct;
        case SharingLinkType.AnonymousView:
            return SharingLinkKind.AnonymousView;
        case SharingLinkType.AnonymousEdit:
            return SharingLinkKind.AnonymousEdit;
        case SharingLinkType.OrganizationView:
            return SharingLinkKind.OrganizationView;
        case SharingLinkType.OrganizationEdit:
            return SharingLinkKind.OrganizationEdit;
        default:
            return assertNever(type);
    }
}
